// components/MaterialView.js - Vista de gestión de pedidos de material
import React, { useState, useEffect } from 'react';
import {
  collection, doc, getDocs, addDoc, updateDoc, setDoc,
  query, where, limit, increment, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Icons } from '../utils/icons';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { getCached, setCached } from '../utils/apiCache';

const MaterialView = () => {
  const { user } = useAuth();
  const [materialOrders, setMaterialOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [stats, setStats] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Recepción en almacén: mapa pedidoId → doc de recepciones_material
  const [recepciones, setRecepciones] = useState({});
  const [recibiendo, setRecibiendo] = useState(false);
  const [recepcionResult, setRecepcionResult] = useState(null); // { pedidoId, lineas: [...] } | { error }

  const cargarRecepciones = async () => {
    try {
      const snap = await getDocs(collection(db, 'recepciones_material'));
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setRecepciones(map);
    } catch (e) {
      console.warn('[MaterialView] no se pudieron cargar las recepciones:', e.message);
    }
  };

  // Registra la entrada de stock de todas las líneas de un pedido de compra.
  // La escritura de recepciones_material es create-only en las reglas: si el
  // pedido ya se recibió, falla ahí antes de tocar el stock (idempotencia).
  const recibirEnAlmacen = async (order) => {
    if (recibiendo) return;
    const pedidoId = String(order.id);
    const lineasItem = (order.lines || []).filter(l => (Number(l.units) || 0) > 0);
    if (lineasItem.length === 0) {
      setRecepcionResult({ error: 'El pedido no tiene líneas con unidades.' });
      return;
    }
    if (!window.confirm(
      `¿Registrar la entrada en almacén de ${lineasItem.length} línea(s) del pedido ${order.reference || pedidoId}?\n\n` +
      `Se sumará el stock de cada producto (creando la ficha si no existe) y quedará registrado como recibido. Esta acción no se puede deshacer.`
    )) return;

    setRecibiendo(true);
    setRecepcionResult(null);
    try {
      const receiptRef = doc(db, 'recepciones_material', pedidoId);
      const payloadLineas = lineasItem.map(l => ({
        referencia:  (l.itemReference || '').trim(),
        itemName:    l.itemName || '',
        itemId:      l.itemId || null,
        units:       Number(l.units) || 0,
        precioCoste: Number(l.itemBasePrice) || 0,
      }));

      try {
        await setDoc(receiptRef, {
          pedidoId,
          referencia:    order.reference || order.fullReference || '',
          proveedor:     order.supplier?.name || '',
          totalLineas:   payloadLineas.length,
          totalUnidades: payloadLineas.reduce((s, l) => s + l.units, 0),
          lineas:        payloadLineas,
          recibidoEn:    serverTimestamp(),
          recibidoPor:   user?.email || user?.name || 'desconocido',
        });
      } catch (e) {
        throw new Error('Este pedido ya consta como recibido en almacén, o faltan permisos (reglas sin desplegar).');
      }

      const resultados = [];
      for (const l of lineasItem) {
        const ref = (l.itemReference || '').trim();
        const units = Number(l.units) || 0;
        try {
          let prod = null;
          if (ref) {
            // Firestore usa la misma estructura de campos que StelOrder
            // (name/reference/real-stock/purchase-price/inactive...), tanto
            // para lo importado como para lo creado a mano — ver AlmacenView.js.
            const snap = await getDocs(query(collection(db, 'productos'), where('reference', '==', ref), limit(1)));
            if (!snap.empty) prod = { id: snap.docs[0].id, ...snap.docs[0].data() };
          }
          let prodId, stockAnterior, creado = false;
          if (!prod) {
            const nuevo = {
              name:             l.itemName || ref || 'Producto sin nombre',
              reference:        ref,
              marca:            order.supplier?.name || '',
              description:      l.itemDescription || '',
              'purchase-price': Number(l.itemBasePrice) || 0,
              'sales-price':    0,
              'real-stock':     units,
              'minimum-stock':  2,
              'stock-enabled':  true,
              location:         '',
              inactive:         false,
              origen:           'app',
              creadoEn:      serverTimestamp(),
              actualizadoEn: serverTimestamp(),
            };
            const added = await addDoc(collection(db, 'productos'), nuevo);
            prodId = added.id; stockAnterior = 0; creado = true;
          } else {
            prodId = prod.id;
            stockAnterior = Number(prod['real-stock']) || 0;
            const upd = { 'real-stock': increment(units), actualizadoEn: serverTimestamp() };
            if (!Number(prod['purchase-price']) && Number(l.itemBasePrice)) upd['purchase-price'] = Number(l.itemBasePrice);
            await updateDoc(doc(db, 'productos', prodId), upd);
          }
          await addDoc(collection(db, 'movimientos_stock'), {
            productoId:     prodId,
            productoNombre: l.itemName || ref || prodId,
            tipo:           'entrada',
            cantidad:       units,
            stockAnterior,
            stockNuevo:     stockAnterior + units,
            notas:          `Recepción pedido ${order.reference || pedidoId}${order.supplier?.name ? ' · ' + order.supplier.name : ''}`,
            fecha:          serverTimestamp(),
          });
          resultados.push({ referencia: ref, itemName: l.itemName, units, creado, ok: true });
        } catch (e) {
          resultados.push({ referencia: ref, itemName: l.itemName, units, ok: false, error: e.message });
        }
      }

      setRecepciones(prev => ({
        ...prev,
        [pedidoId]: { id: pedidoId, pedidoId, proveedor: order.supplier?.name || '', totalLineas: payloadLineas.length, recibidoPor: user?.email || user?.name || '', recibidoEn: { seconds: Math.floor(Date.now() / 1000) } },
      }));
      setRecepcionResult({ pedidoId, lineas: resultados });
    } catch (e) {
      setRecepcionResult({ error: e.message });
    } finally {
      setRecibiendo(false);
    }
  };

  // Cargar pedidos de material
  const loadMaterialOrders = async (forceRefresh = false) => {
    const CACHE_KEY = 'material_orders';

    if (!forceRefresh) {
      const cached = getCached(CACHE_KEY);
      if (cached) {
        setMaterialOrders(cached.data.materialOrders || []);
        setStats(cached.data.stats || {});
        setLastUpdated(new Date(cached.ts));
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError("");
    setShowSuccess(false);
    setMaterialOrders([]);
    setStats({});

    try {
      console.log("📦 Cargando pedidos de material desde StelOrder...");

      const fetchOpts = { headers: { ...authService.authHeader() }, ...(forceRefresh ? { cache: 'reload' } : {}) };
      const response = await fetch(`/.netlify/functions/getMaterialOrders`, fetchOpts);
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
        return;
      }
      
      setCached(CACHE_KEY, data);
      setMaterialOrders(data.materialOrders || []);
      setStats(data.stats || {});
      setLastUpdated(new Date());

      // 🎉 Mostrar notificación de éxito
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      console.log("✅ Pedidos de material cargados:", data.materialOrders?.length || 0);
      console.log("📊 Estadísticas:", data.stats);
      
    } catch (err) {
      console.error("❌ Error cargando pedidos:", err);
      setError(`Error cargando pedidos: ${err.message}`);
      // En caso de error, mantener datos anteriores si los hay
    } finally {
      setLoading(false);
    }
  };

  // Generar Excel para Ariston (mejorado - pasar datos del pedido)
  const generateAristonExcel = async (orderId) => {
    setGeneratingExcel(true);
    
    try {
      console.log(`📋 Generando Excel para orden: ${orderId}`);
      
      // 🆕 Encontrar la orden en nuestros datos locales
      const orderData = materialOrders.find(order => order.id === orderId);
      
      if (!orderData) {
        throw new Error('Orden no encontrada en los datos locales');
      }
      
      console.log(`📦 Orden encontrada localmente con ${orderData.lines?.length || 0} productos`);
      
      const response = await fetch('/.netlify/functions/generateAristonExcel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.authHeader(),
        },
        body: JSON.stringify({ 
          orderId,
          orderData: orderData // 🆕 Pasar los datos completos
        })
      });
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Error generando Excel');
      }
      
      // 📥 Descargar el archivo
      const csvContent = atob(data.fileContent);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log("✅ Excel descargado:", data.fileName);
      setShowModal(false);
      setSelectedOrder(null);
      
    } catch (err) {
      console.error("❌ Error generando Excel:", err);
      setError(`Error generando Excel: ${err.message}`);
    } finally {
      setGeneratingExcel(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    loadMaterialOrders();
    cargarRecepciones();
  }, []);

  // Al cambiar de pedido seleccionado, limpia el resultado de recepción previo
  useEffect(() => { setRecepcionResult(null); }, [selectedOrder?.id]);

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatear precio
  const formatPrice = (amount) => {
    if (typeof amount !== 'number') return '0,00 €';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Calcular tiempo de procesamiento mejorado
  const calculateProcessingTime = (creationDate, lastModificationDate, documentStateId) => {
    if (!creationDate) return null;
    
    const created = new Date(creationDate);
    if (isNaN(created.getTime())) return null;
    
    // Determinar fecha de fin según el estado
    let endDate;
    let isCompleted = false;
    
    if (documentStateId === 651463) { // Recibido
      endDate = new Date(lastModificationDate);
      isCompleted = true;
    } else {
      endDate = new Date(); // Fecha actual para pedidos en proceso
      isCompleted = false;
    }
    
    // Verificar que la fecha de fin sea válida y posterior a la creación
    if (isNaN(endDate.getTime()) || endDate <= created) {
      return null;
    }
    
    const diffMs = endDate - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    let timeText = '';
    if (diffDays > 0) {
      timeText = `${diffDays} día${diffDays > 1 ? 's' : ''}${diffHours > 0 ? ` y ${diffHours}h` : ''}`;
    } else if (diffHours > 0) {
      timeText = `${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    } else {
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      timeText = `${diffMinutes} min`;
    }
    
    return {
      timeText,
      isCompleted,
      icon: isCompleted ? '✅' : '⏳',
      label: isCompleted ? 'Procesado en:' : 'Tiempo abierto:',
      color: isCompleted ? 'text-green-600' : 'text-blue-600'
    };
  };
  const getOrderStatus = (order) => {
    if (order.documentState) {
      return {
        label: order.documentState.name,
        color: order.documentState.color,
        icon: order.documentState.icon,
        priority: order.documentState.priority
      };
    }
    
    // Fallback al sistema anterior
    if (order.settled) {
      return { label: 'Liquidado', color: 'bg-green-100 text-green-800', icon: '✅', priority: 3 };
    }
    if (order.signed) {
      return { label: 'Firmado', color: 'bg-blue-100 text-blue-800', icon: '✍️', priority: 2 };
    }
    return { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: '⏳', priority: 1 };
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            📦 Gestión de Material
            {loading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
            )}
          </h2>
          <p className="text-gray-600 mt-1">
            Pedidos de material de StelOrder
            {lastUpdated && (
              <span className="text-xs text-gray-500 ml-2">
                · Actualizado {Math.floor((Date.now() - lastUpdated) / 60000) < 1 ? 'ahora mismo' : `hace ${Math.floor((Date.now() - lastUpdated) / 60000)} min`}
              </span>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadMaterialOrders(true)}
            className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={loading}
            title="Actualizar pedidos"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="text-sm">Cargando...</span>
              </>
            ) : (
              <>
                🔄
                <span className="text-sm hidden sm:inline">Actualizar</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notificación de éxito */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg animate-pulse">
          ✅ Pedidos actualizados desde StelOrder
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
          ❌ {error}
          <button 
            onClick={loadMaterialOrders}
            className="ml-4 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Estadísticas */}
      {stats && Object.keys(stats).length > 0 && !loading && (
        <div className="bg-gradient-to-r from-red-50 to-gray-50 rounded-xl p-4 border border-red-200 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">📊 Resumen</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg p-3 border text-center">
              <div className="text-2xl font-bold text-gray-800">{stats.totalOrders || 0}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-white rounded-lg p-3 border text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.pendientesRecibir || 0}</div>
              <div className="text-xs text-gray-500">Pendientes</div>
            </div>
            <div className="bg-white rounded-lg p-3 border text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.enCurso || 0}</div>
              <div className="text-xs text-gray-500">En Curso</div>
            </div>
            <div className="bg-white rounded-lg p-3 border text-center">
              <div className="text-2xl font-bold text-green-600">{stats.recibidos || 0}</div>
              <div className="text-xs text-gray-500">Recibidos</div>
            </div>
            <div className="bg-white rounded-lg p-3 border text-center">
              <div className="text-2xl font-bold text-gray-800">{stats.totalItems || 0}</div>
              <div className="text-xs text-gray-500">Productos</div>
            </div>
            <div className="bg-white rounded-lg p-3 border text-center">
              <div className="text-2xl font-bold text-red-600">{formatPrice(stats.totalAmount || 0).replace(' €', '')}</div>
              <div className="text-xs text-gray-500">Valor Total</div>
            </div>
          </div>
        </div>
      )}

      {/* Estado de carga */}
      {loading && (
        <div className="bg-red-50 border border-red-200 p-6 rounded-xl mb-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-red-800 font-medium">Cargando pedidos de StelOrder...</p>
          <p className="text-red-600 text-sm mt-1">Consultando purchase orders...</p>
        </div>
      )}

      {/* Lista de pedidos */}
      {!loading && materialOrders.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            📋 Pedidos de Material ({materialOrders.length})
          </h3>
          
          <div className="grid gap-4">
            {materialOrders.map((order, idx) => {
              const status = getOrderStatus(order);
              const isPending = order.documentStateId === 651439; // Pendiente de recibir
              const processingTime = calculateProcessingTime(
                order.creationDate, 
                order.lastModificationDate, 
                order.documentStateId
              );
              
              return (
                <div 
                  key={order.id}
                  className={`bg-gray-50 border rounded-xl p-4 hover:shadow-md transition-all duration-200 cursor-pointer hover:border-gray-300 ${
                    isPending ? 'ring-2 ring-yellow-400 ring-opacity-50 bg-yellow-50' : ''
                  }`}
                  onClick={() => {
                    setSelectedOrder(order);
                    setShowModal(true);
                  }}
                >
                  <div className="flex justify-between items-start">
                    {/* Información principal */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {isPending && (
                          <div className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                            ⚡ PRIORIDAD
                          </div>
                        )}
                        <h4 className="font-semibold text-gray-800">
                          {order.reference || `Pedido #${order.id}`}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.icon} {status.label}
                        </span>
                        {recepciones[String(order.id)] && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            📥 En almacén
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                        <div>
                          <strong>Proveedor:</strong> {order.supplier?.name || 'Sin proveedor'}
                          {order.extraReference && (
                            <div className="text-xs text-gray-500 mt-1">
                              Ref. Proveedor: {order.extraReference}
                            </div>
                          )}
                        </div>
                        <div>
                          <strong>Fecha:</strong> {formatDate(order.date)}
                          {processingTime && (
                            <div className={`text-xs mt-1 font-medium ${processingTime.color}`}>
                              {processingTime.icon} {processingTime.label} {processingTime.timeText}
                            </div>
                          )}
                        </div>
                        <div>
                          <strong>Productos:</strong> {order.lines?.length || 0} items
                        </div>
                      </div>

                      {order.comments && (
                        <div className="mt-2 text-xs text-gray-500 bg-white p-2 rounded border-l-4 border-gray-300">
                          💬 {order.comments}
                        </div>
                      )}
                    </div>
                    
                    {/* Importe y acciones */}
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-gray-800">
                        {formatPrice(order.totalAmount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        ({order.lines?.length || 0} productos)
                      </div>
                      
                      {/* Botones de acción rápida */}
                      <div className="flex gap-2 mt-3 justify-end">
                        {order.pdfPath && (
                          <button 
                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(order.pdfPath, '_blank');
                            }}
                            title="Ver PDF del pedido"
                          >
                            📄
                          </button>
                        )}
                        <button 
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                            setShowModal(true);
                          }}
                          title="Ver detalles y generar Excel"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Estado vacío */}
      {!loading && materialOrders.length === 0 && !error && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No hay pedidos de material</h3>
          <p className="text-sm">No se encontraron purchase orders en StelOrder</p>
          <button
            onClick={loadMaterialOrders}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            🔄 Recargar
          </button>
        </div>
      )}

      {/* Modal de confirmación para generar Excel */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header del modal */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    📋 {selectedOrder.reference || `Pedido #${selectedOrder.id}`}
                  </h3>
                  <p className="text-gray-600 mt-1">
                    {selectedOrder.supplier?.name || 'Sin proveedor'} • {formatDate(selectedOrder.date)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={generatingExcel}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              {/* Información del pedido */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">📊 Información del Pedido</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div><strong>Total:</strong> {formatPrice(selectedOrder.totalAmount)}</div>
                    <div><strong>Estado:</strong> {getOrderStatus(selectedOrder).label}</div>
                    <div><strong>Productos:</strong> {selectedOrder.lines?.length || 0} items</div>
                    <div><strong>Descuento:</strong> {selectedOrder.discountPercentage}%</div>
                  </div>
                  
                  {/* 🆕 Tiempo de procesamiento mejorado */}
                  {(() => {
                    const processingTime = calculateProcessingTime(
                      selectedOrder.creationDate, 
                      selectedOrder.lastModificationDate, 
                      selectedOrder.documentStateId
                    );
                    return processingTime ? (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <strong>{processingTime.icon} {processingTime.label}</strong> 
                          <span className={`font-medium ${processingTime.color}`}>{processingTime.timeText}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {processingTime.isCompleted ? (
                            <>Desde creación ({formatDate(selectedOrder.creationDate)}) hasta recepción ({formatDate(selectedOrder.lastModificationDate)})</>
                          ) : (
                            <>Desde creación ({formatDate(selectedOrder.creationDate)}) hasta ahora (en proceso)</>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {selectedOrder.comments && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <strong>Comentarios:</strong> {selectedOrder.comments}
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de productos */}
              {selectedOrder.lines && selectedOrder.lines.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">📦 Productos ({selectedOrder.lines.length})</h4>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div className="space-y-2">
                      {selectedOrder.lines.map((line, idx) => (
                        <div key={line.id || idx} className="bg-white p-3 rounded border text-sm">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">
                                {line.itemName || 'Producto sin nombre'}
                              </div>
                              <div className="text-gray-600">
                                Código: {line.itemReference || `ITEM-${line.itemId}`}
                              </div>
                              {line.itemDescription && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {line.itemDescription}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <div className="font-medium">
                                {line.units} uds
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatPrice(line.totalAmount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Recepción en almacén */}
              {(() => {
                const rec = recepciones[String(selectedOrder.id)];
                const res = recepcionResult && recepcionResult.pedidoId === String(selectedOrder.id) ? recepcionResult : null;
                return (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">📥 Recepción en almacén</h4>
                    {rec ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900">
                        <p className="font-semibold">✅ Recibido en almacén</p>
                        <p className="text-emerald-700 text-xs mt-1">
                          {rec.totalLineas != null && <>{rec.totalLineas} línea(s) · </>}
                          {rec.recibidoPor && <>por {rec.recibidoPor} · </>}
                          {rec.recibidoEn?.seconds
                            ? new Date(rec.recibidoEn.seconds * 1000).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                            : ''}
                        </p>
                        {res && (
                          <ul className="mt-2 space-y-0.5 text-xs">
                            {res.lineas.map((l, i) => (
                              <li key={i} className={l.ok ? 'text-emerald-800' : 'text-red-700'}>
                                {l.ok ? (l.creado ? '🆕' : '➕') : '⚠️'} {l.itemName || l.referencia || 'línea'} · {l.units} uds
                                {l.creado && ' (producto nuevo)'}
                                {!l.ok && ` — ${l.error}`}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-3">
                          Suma al stock del almacén las unidades de cada línea de este pedido.
                          Si un producto no existe todavía, se crea su ficha automáticamente.
                        </p>
                        {recepcionResult?.error && (
                          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">
                            ❌ {recepcionResult.error}
                          </p>
                        )}
                        <button
                          onClick={() => recibirEnAlmacen(selectedOrder)}
                          disabled={recibiendo}
                          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                        >
                          {recibiendo ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Registrando entrada...
                            </>
                          ) : (
                            <>📥 Recibir en almacén</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Pregunta de confirmación */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="text-red-500 text-xl">📊</div>
                  <div>
                    <h4 className="font-semibold text-red-800 mb-2">
                      ¿Crear pedido de material para Ariston?
                    </h4>
                    <p className="text-red-700 text-sm mb-3">
                      Se generará un archivo Excel (.csv) con la estructura requerida por Ariston 
                      que contiene únicamente los códigos de producto y las cantidades.
                    </p>
                    <div className="text-xs text-red-600 bg-red-100 p-2 rounded">
                      <strong>Estructura:</strong> codigo, cantidad<br/>
                      <strong>Productos:</strong> {selectedOrder.lines?.length || 0} items<br/>
                      <strong>Formato:</strong> CSV compatible con Excel
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={generatingExcel}
                >
                  Cancelar
                </button>
                
                {/* 🆕 Botón Ver PDF */}
                {selectedOrder.pdfPath && (
                  <button
                    onClick={() => {
                      window.open(selectedOrder.pdfPath, '_blank');
                    }}
                    className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
                    disabled={generatingExcel}
                  >
                    📄 Ver PDF
                  </button>
                )}
                
                <button
                  onClick={() => generateAristonExcel(selectedOrder.id)}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={generatingExcel}
                >
                  {generatingExcel ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Generando...
                    </>
                  ) : (
                    <>
                      📊 Generar Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialView;